import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Phone, MessageCircle, MapPin, Clock, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PHONE, TEL_URL, WHATSAPP_URL } from "@/lib/api";

const packages = [
  {
    name: "Ghatagaon Tarini Darshan",
    duration: "Half Day",
    distance: "~50 km",
    img: "https://upload.wikimedia.org/wikipedia/commons/6/6b/Tarni_temple_Gate_view.JPG",
    blurb:
      "Round-trip to Maa Tarini Temple in Ghatagaon. Pickup from Harichandanpur, comfortable waiting time at the temple and a safe drop back.",
    includes: ["AC sedan / SUV", "Driver allowance", "All fuel & tolls", "Temple waiting time"],
    tag: "Pilgrimage",
    price: 2799,
  },
  {
    name: "Khandadhar Falls Day Trip",
    duration: "Full Day",
    distance: "~140 km",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Khandadhar.jpg/1280px-Khandadhar.jpg",
    blurb:
      "Witness one of Odisha&apos;s tallest tiered waterfalls. We arrange a relaxed day with stops at viewpoints and lunch en route.",
    includes: ["AC vehicle", "Experienced driver", "Multi-stop", "Sunrise / sunset views"],
    tag: "Nature",
    price: 3499,
  },
  {
    name: "Keonjhar Local Sightseeing",
    duration: "Full Day",
    distance: "~120 km",
    img: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Sanaghagara_waterfall.jpg/1280px-Sanaghagara_waterfall.jpg",
    blurb:
      "Sanaghagara, Murga Mahadev, Gonasika and the iron-ore belt forests — a full taste of Keonjhar in one day.",
    includes: ["SUV recommended", "Local guide on request", "Forest entry support", "Photo stops"],
    tag: "Local Circuit",
    price: 3199,
  },
  {
    name: "Bhubaneswar / Cuttack Drop",
    duration: "1 Way / Round Trip",
    distance: "~210 km",
    img: "https://images.unsplash.com/photo-1534263873534-4d0d76aae42b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzY2VuaWMlMjBmb3Jlc3QlMjByb2FkJTIwZHJpdmluZyUyMGluZGlhfGVufDB8fHx8MTc4MDk5MTU0MHww&ixlib=rb-4.1.0&q=85",
    blurb:
      "Door-to-door taxi from Keonjhar to BBI airport, Lingaraj Temple, hospitals or business meetings — on your schedule.",
    includes: ["Sedan / SUV / Innova", "Airport pickup & drop", "Hospital visits", "On-demand stops"],
    tag: "Outstation",
    price: 4499,
  },
  {
    name: "Puri – Konark Pilgrim Tour",
    duration: "2 Days",
    distance: "~600 km",
    img: "https://images.unsplash.com/photo-1494472155656-f34e81b17ddc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHx3YXRlcmZhbGwlMjBuYXR1cmV8ZW58MHx8fHwxNzgwOTkxNTUxfDA&ixlib=rb-4.1.0&q=85",
    blurb:
      "A blessed circuit — Jagannath Temple in Puri, Konark Sun Temple, and Chilika lake. Comfortable, family-friendly itinerary.",
    includes: ["AC SUV / Innova", "Driver night halt", "All toll & parking", "Custom itinerary"],
    tag: "Pilgrimage",
    price: 8999,
  },
  {
    name: "Jamshedpur / Rourkela Run",
    duration: "1 Day",
    distance: "~250 km",
    img: "https://images.pexels.com/photos/17612417/pexels-photo-17612417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    blurb:
      "Cross-state runs to Jamshedpur, Rourkela and surrounding industrial hubs — meetings, family, hospitals.",
    includes: ["AC sedan / SUV", "On-time pickup", "Highway routes", "Flexible halts"],
    tag: "Outstation",
    price: 4999,
  },
];

const TourPackages = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Tour Packages · Keonjhar, Ghatagaon, Khandadhar Taxi Tours | Amyee Travels";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="App bg-[#0A0E17] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link
            to="/"
            data-testid="back-home-link"
            className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] hover:text-white inline-flex items-center gap-2 mb-8"
          >
            ← Back home
          </Link>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Tour packages</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95] tracking-tight max-w-5xl">
            Pre-built journeys
            <br />
            <span className="italic text-[#D95D39]">across Keonjhar.</span>
          </h1>
          <p className="mt-8 text-gray-400 max-w-2xl leading-relaxed">
            Hand-crafted routes that locals actually take. Every package is flexible — call us
            and we&apos;ll adjust the stops, vehicle and duration to suit you.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={TEL_URL}
              data-testid="packages-call-button"
              className="bg-[#D95D39] hover:bg-[#E27D60] text-white px-7 py-3.5 flex items-center gap-3 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="tracking-wider">Call {PHONE}</span>
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="packages-whatsapp-button"
              className="border border-[#2C5F2D] bg-[#2C5F2D]/30 hover:bg-[#2C5F2D] text-white px-7 py-3.5 flex items-center gap-3 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="tracking-wider">WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* PACKAGES GRID */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {packages.map((p, i) => (
              <article
                key={i}
                data-testid={`package-card-${i}`}
                className="group relative bg-[#131B2A] border border-white/5 hover:border-[#D4AF37]/40 transition-colors overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-64 lg:h-full overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="text-[10px] uppercase tracking-widest bg-[#0A0E17]/80 backdrop-blur-sm text-[#D4AF37] px-3 py-1.5 border border-[#D4AF37]/40">
                        {p.tag}
                      </span>
                    </div>
                  </div>

                  <div className="p-8 lg:p-10 flex flex-col">
                    <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight">{p.name}</h3>

                    <div className="flex flex-wrap gap-5 mt-4 text-sm text-gray-400">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#D4AF37]" /> {p.duration}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#D4AF37]" /> {p.distance}
                      </span>
                    </div>

                    <p className="text-gray-400 leading-relaxed mt-5 text-sm">{p.blurb}</p>

                    <ul className="mt-5 space-y-2">
                      {p.includes.map((inc, k) => (
                        <li key={k} className="text-xs text-gray-500 flex items-start gap-2">
                          <span className="text-[#D95D39] mt-1">◆</span>
                          {inc}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-7 flex flex-wrap gap-3">
                      <a
                        href={TEL_URL}
                        data-testid={`package-call-button-${i}`}
                        className="bg-[#D95D39] hover:bg-[#E27D60] text-white px-5 py-2.5 text-sm flex items-center gap-2 transition-colors"
                      >
                        <Phone className="w-3.5 h-3.5" /> Get a Quote
                      </a>
                      <Link
                        to="/#booking"
                        data-testid={`package-book-button-${i}`}
                        className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E17] px-5 py-2.5 text-sm flex items-center gap-2 transition-colors"
                      >
                        Book <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#131B2A] py-20 md:py-28 border-y border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Custom trip?</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-white tracking-tight">
            Don&apos;t see your route? <span className="italic text-[#D95D39]">Just call us.</span>
          </h2>
          <p className="mt-5 text-gray-400 max-w-xl mx-auto">
            We design custom multi-day Odisha tours, family trips and corporate transfers tailored to your dates.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href={TEL_URL}
              data-testid="cta-call-button"
              className="bg-[#D95D39] hover:bg-[#E27D60] text-white px-8 py-4 flex items-center gap-3 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="tracking-wider">Call {PHONE}</span>
            </a>
            <Link
              to="/#booking"
              data-testid="cta-book-button"
              className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E17] px-8 py-4 flex items-center gap-3 transition-colors"
            >
              Book Online <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TourPackages;
